import * as React from 'react';
import { GeneralSettings, Settings } from '../Model';
import { NotificationBox, Box, NotificationAlert, NotificationType } from '../CommonUI';
import Button from '../CommonUI/Control/Button';
import { GeneralList, GeneralListItem, ProductReviewsToggle } from '../Components';
import '../Assets/styles/component.css';
import '../Assets/styles/general.css';
import { Flex } from '../CommonUI/Layout';
import { Text, HtmlText } from 'ilenia';
import { Redirect } from 'react-router-dom';
import { StyleSheet, css } from 'aphrodite';
import { getIntegrationConfig } from '../Api/configapiClient';

interface GeneralProps {
    settings: Settings;
    source: string;
    onChange: (settings: GeneralSettings) => void;
    onSettingsSave: () => void;
    showUpdateNotification?: boolean;
    onShowUpdateNotificationClose: () => void;
    onLoggedInChange: (toggle: boolean) => void;
    tierName: string;
    onProductReviewsSettingChange: (selector: Settings) => void;
    onConsentChange: (toggle: boolean) => void;
    onToggleSettingsSave: (value: boolean) => void;
}

interface GeneralState {
    path?: string;
    enableProductReviews: boolean;
}

export class General extends React.Component<GeneralProps, GeneralState> {
    constructor(props: GeneralProps) {
        super(props);
        this.state = {
            path: undefined,
            enableProductReviews: false,
        };
        this.redirect = this.redirect.bind(this);
        this.toggleChanged = this.toggleChanged.bind(this);
    }

    public componentDidMount() {
        analytics.page();
        const { settings } = this.props;
        const { general } = settings;
        const { key } = general!;
        if (key) {
            getIntegrationConfig(key).then((response) => {
                const enableProductReviews = (response && response.enableProductReviews) || false;
                if (this.state.enableProductReviews !== enableProductReviews) {
                    this.setState({enableProductReviews});
                }
            });
        }
    }

    public redirect(path: string) {
        return () => this.setState({ path });
    }

    public toggleChanged(value: boolean) {
        const enableProductReviews = value;
        if (enableProductReviews) {
            NotificationAlert.alert(NotificationType.Success, 'Notifications.productReviewsEnabled');
        }
        this.setState({ enableProductReviews });
    }

    public render() {
        const { path } = this.state;
        if (path) {
            return <Redirect to={path} push={true}/>;
        }

        const { onLoggedInChange, source, onSettingsSave, showUpdateNotification, onShowUpdateNotificationClose,
                tierName, settings, onConsentChange, onToggleSettingsSave } = this.props;
        const { pastOrders } = settings;

        const productReviewDescription = (
            <span>
                <span className={css(styles.greenText)}><Text id="general.productReview.descriptionPercentage"/></span>
                <Text id="general.productReview.description" />
            </span>
        );

        const trustBoxDescription = (
            <span>
                <span className={css(styles.greenText)}><Text id="general.trustBox.descriptionPercentage"/></span>
                <Text id="general.trustBox.description" />
            </span>
        );

        const serviceReviewButton = () => {
            return (
                <Button
                    secondaryButton={true}
                    onClick={this.redirect('/invitations')}
                    pl={0}
                    pr={0}
                    mt={0}
                    minWidth="102px"
                    width="auto"
                    className={css(styles.button)}
                    track={{
                        placement: 'General tab form',
                        action: `Open invitation tab`,
                        name: `open-invitation-tab`,
                        source: this.props.source,
                        tierName,
                    }}
                >
                    <Text id="general.serviceReview.button" />
                </Button>
            );
        };

        const productReviewToggle = (
            <ProductReviewsToggle
                integrationKey={settings.general!.key!}
                source={source}
                tierName={tierName}
                onLoggedInChange={onLoggedInChange}
                onSettingsSave={onSettingsSave}
                toggleChanged={this.toggleChanged}
                sku={settings.skuSelector!}
                onProductReviewsSettingChange={this.props.onProductReviewsSettingChange}
                productIdentificationOptions={settings.productIdentificationOptions}
                onConsentChange={onConsentChange}
                onToggleSettingsSave={onToggleSettingsSave}
            />
        );

        const trustBoxButton = () => {
            const hasTrustBoxes = this.hasTrustBoxes();
            return (
                <Button
                    disabled={false}
                    secondaryButton={hasTrustBoxes}
                    onClick={this.redirect('/trustbox')}
                    pl={0}
                    pr={0}
                    mt={0}
                    className={css(styles.button)}
                    minWidth="102px"
                    width="auto"
                    track={{
                        placement: 'General tab form',
                        action: `${hasTrustBoxes ? 'Open' : 'Start'} TrustBox preview`,
                        name: `${hasTrustBoxes ? 'open' : 'start'}-trustbox-preview`,
                        source: this.props.source,
                        tierName,
                    }}
                >
                    {hasTrustBoxes ? <Text id="general.trustBox.settings" /> : <Text id="general.trustBox.start" />}
                </Button>
            );
        };

        const kickstarted = Boolean(pastOrders && (!pastOrders.showInitial || pastOrders.syncInProgress));

        return (
            <Flex>
                {showUpdateNotification ?
                    <Box mt={33} className={css(styles.notificationBar)}>
                        <NotificationBox text="general.updateNotification" onClick={onShowUpdateNotificationClose}/>
                    </Box> : ''
                }
                <GeneralList title="general.header.title" description="general.header.description">
                    <GeneralListItem
                        actionColumn={serviceReviewButton()}
                        label="general.serviceReview.label"
                        description="general.serviceReview.description"
                        checked={true}
                    />
                    <GeneralListItem
                        actionColumn={productReviewToggle}
                        label="general.productReview.label"
                        description={productReviewDescription}
                        checked={this.state.enableProductReviews}
                    />
                    <GeneralListItem
                        checked={kickstarted}
                        label={<Text id="general.kickstart.label" />}
                        description={
                            <HtmlText
                                id="general.kickstart.description"
                                interpolations={{ c1: `<span key="description" class=${css(styles.greenText)}>`, c: '</span>' }}
                            />}
                        actionColumn={
                            <Button
                                className={css(styles.button)}
                                secondaryButton={kickstarted}
                                onClick={this.redirect('/past-orders')}
                                pl={0}
                                pr={0}
                                mt={0}
                                minWidth="102px"
                                width="auto"
                                track={{
                                    placement: 'General tab form',
                                    action: `${kickstarted ? 'Open' : 'Start'} past orders`,
                                    name: `${kickstarted ? 'open' : 'start'}-past-orders`,
                                    source: this.props.source,
                                    tierName,
                                }}
                            >
                                <Text id={kickstarted ? 'general.kickstart.buttonSettings' : 'general.kickstart.buttonStart'} />
                            </Button>}
                    />
                    <GeneralListItem
                        actionColumn={trustBoxButton()}
                        label="general.trustBox.label"
                        description={trustBoxDescription}
                        checked={this.hasTrustBoxes()}
                    />
                </GeneralList>
            </Flex>
        );
    }

    private hasTrustBoxes = () => {
        const { trustbox } = this.props.settings;
        return trustbox ? trustbox.trustboxes.length > 0 : false;
    }
}

const styles = StyleSheet.create({
    greenText: {
        color: 'rgb(0, 182, 122)',
        fontWeight: 'bold',
    },
    button: {
        display: 'flex',
        justifyContent: 'center',
        whiteSpace: 'nowrap',
    },
    notificationBar: {
        margin: '0px auto',
        width: '100%',
        maxWidth: '800px',
    }
});
